import { useCallback, useEffect, useRef, useState } from 'react';
import PlatformService from 'services/platform.service';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Focus, visibility and reconnect fire in bursts; one check per burst is enough.
const MIN_EVENT_GAP_MS = 30 * 1000;

// Set at build time by scripts/write-version.js; missing in tests, where the check stays off.
const RUNNING_VERSION = process.env.REACT_APP_VERSION;

/**
 * Watches for a newer deployed build. Compares the running build with /version.json every 5 minutes,
 * when the window regains focus or becomes visible, and when the connection comes back; on a mismatch
 * the API (`platform_app_versions/current.web`) decides whether the update is optional or required.
 * Returns `null` or `{ required, message, deployedVersion }`. It keeps checking after an answer, so a
 * forced update turned on later still reaches the tab.
 */
export const useReleaseCheck = () => {
    const [update, setUpdate] = useState(null);
    const checking = useRef(false);
    const lastCheckAt = useRef(0);

    const check = useCallback(async () => {
        if (!RUNNING_VERSION || checking.current) return;
        checking.current = true;
        lastCheckAt.current = Date.now();

        try {
            const response = await fetch('/version.json', { cache: 'no-store' });
            if (!response.ok) return;
            const { version: deployedVersion } = await response.json();

            if (!deployedVersion || deployedVersion === RUNNING_VERSION) {
                setUpdate(null);
                return;
            }

            const { force_update: forceUpdate, message } =
                await PlatformService.checkWebUpdate(RUNNING_VERSION, deployedVersion);
            setUpdate(
                typeof forceUpdate === 'boolean'
                    ? { required: forceUpdate, message, deployedVersion }
                    : null
            );
        } catch (error) {
            // Offline or API unavailable — the next check tries again.
        } finally {
            checking.current = false;
        }
    }, []);

    useEffect(() => {
        const checkOnEvent = () => {
            if (Date.now() - lastCheckAt.current >= MIN_EVENT_GAP_MS) check();
        };
        const checkWhenVisible = () => {
            if (document.visibilityState === 'visible') checkOnEvent();
        };

        check();
        const interval = setInterval(check, CHECK_INTERVAL_MS);
        window.addEventListener('focus', checkOnEvent);
        window.addEventListener('online', checkOnEvent);
        document.addEventListener('visibilitychange', checkWhenVisible);

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', checkOnEvent);
            window.removeEventListener('online', checkOnEvent);
            document.removeEventListener('visibilitychange', checkWhenVisible);
        };
    }, [check]);

    return update;
};
